import React, { useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { themeLight } from '@digital-hig/mui/dist/Theme';
import { window, document } from 'global';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { getMessages } from '../store/messagingSlice';
import Toast from './toastHOC';
import { deserializeElement } from '../common/utilities';
import { getErrorState } from '../store/errorSlice';
import ErrorComponent from './error';
import GlobalSubNav from './globalSubNav';
import { fetchListsData, getUserDetails, hasFetchedLists } from '../store/userSlice';
import EnvContext from '../common/envContext';
import { ENVIRONMENT, VERSION } from '../common/constants';

const light = createTheme(themeLight);

// If your page needs a ref to the subnav to measure its location and size etc pass in subNavRef and it will be used.
const PageWrapper = ({
  children, className, location, subNavRef,
}) => {
  const messages = useSelector(getMessages);
  const errorState = useSelector(getErrorState);
  const globalNav = document?.getElementsByClassName('uh-container-wrapper');
  const globalNavRef = useRef();
  const userDetails = useSelector(getUserDetails);
  const { oa2Token } = userDetails;
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const appVersion = envContext[VERSION];
  const hasFetchedListData = useSelector(hasFetchedLists);
  const dispatch = useDispatch();

  if (globalNav?.length) {
    // eslint-disable-next-line prefer-destructuring
    globalNavRef.current = globalNav[0];
  }
  const defaultSubNavRef = useRef();
  if (!subNavRef) {
    // eslint-disable-next-line no-param-reassign
    subNavRef = defaultSubNavRef;
  }

  useEffect(() => {
    if (oa2Token && !hasFetchedListData) {
      dispatch(fetchListsData({ oa2Token, env, appVersion }));
    }
  }, [userDetails]);

  const positionLocalNav = () => {
    setTimeout(() => {
      if (globalNav?.length && subNavRef?.current) {
        const topDistance = globalNav[0].offsetHeight;
        // eslint-disable-next-line no-param-reassign
        subNavRef.current.style.top = `${topDistance}px`;
      }
    }, 10);
    setTimeout(() => {
      if (globalNav?.length && subNavRef?.current) {
        const topDistance = globalNav[0].offsetHeight;
        // eslint-disable-next-line no-param-reassign
        subNavRef.current.style.top = `${topDistance}px`;
      }
    }, 1000);
  };

  useEffect(() => {
    positionLocalNav();
    window.addEventListener('resize', positionLocalNav);

    return () => {
      window.removeEventListener('resize', positionLocalNav);
    };
  }, []);

  return (
    <ThemeProvider theme={light}>
      <Helmet key="helmet">
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      <RootElement className={`${className} style-override`}>
        <div className="toasts">
          {messages?.map((message) => (<Toast type={message.type} id={message.id} closeEvent={message.closeEvent || false}>{deserializeElement(message.children)}</Toast>))}
        </div>
        <div className="subNav-container">
          <div id="subNav" ref={subNavRef}><GlobalSubNav /></div>
        </div>
        <div id="contentRoot" className="page-content">
          { errorState.hasError ? <ErrorComponent location={location} /> : children }
        </div>
      </RootElement>
    </ThemeProvider>
  );
};

const RootElement = styled.div`
  position:relative;
  .subNav-container {
    #subNav {
      width: 100%;
      background: #f9f9f9;
      border-bottom: 1px solid rgb(229, 229, 229);
    }
  }
 
  h1, h2 {
    font-family: 'ArtifaktLegend';
  }
  
    &.catalog {
      background-color: #f9f9f9;
    }
    
     .toasts {
      position:fixed;
      z-index: 200;
      right: 20px;
      top: 140px;
    }
 `;

PageWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.arrayOf(PropTypes.shape({}))]).isRequired,
  className: PropTypes.string,
  location: PropTypes.shape({}).isRequired,
  subNavRef: PropTypes.shape({
    current: PropTypes.element,
  }),
};

PageWrapper.defaultProps = {
  className: '',
  subNavRef: undefined,
};

export default PageWrapper;
